import { TaggedUnion } from 'type-fest';
import z from 'zod';
import { ChartOfAccountSchema } from '../chart-of-accounts/chartOfAccountsTypes';
import { QcsResourceSlice } from '../core/resourceSliceUtils';

export const TRANSACTION_RULES_REDUCER_KEY = 'transactionRulesStore';
export type TRANSACTION_RULES_REDUCER_KEY = typeof TRANSACTION_RULES_REDUCER_KEY;

export const EDITING = 'EDITING';
export const VALIDATING = 'VALIDIATING';
export const RESOLVING = 'RESOLVING';
export const VALIDATED = 'VALIDATED';
export const SUBMITTING = 'SUBMITTING';
export const SUBMITTED = 'SUBMITTED';
export const CLEARED = 'CLEARED';

export type TransactionRulesStages =
  | typeof EDITING
  | typeof VALIDATING
  | typeof RESOLVING
  | typeof VALIDATED
  | typeof SUBMITTING
  | typeof SUBMITTED
  | typeof CLEARED;

const usageTypeSchema = z.enum(['PERSONAL', 'BUSINESS']);
const payeeNameTypeSchema = z.enum(['STATEMENT', 'INFERRED']);
const payeeMatchingOperatorSchema = z.enum(['CONTAINS', 'EQUALS']);
const amountMatchingOperatorSchema = z.enum(['EQUALS', 'GREATER_THAN', 'LESS_THAN', 'BETWEEN']);
const serviceLevel = z.enum(['BASIC', 'PREMIUM']);

const payeeMatchingCriteriaSchema = z.object({
  payeeMatchingOperator: payeeMatchingOperatorSchema,
  payeeName: z.string().optional(),
});

const payeeSourceConditionSchema = z.object({
  payeeType: payeeNameTypeSchema,
  payeeMatchingCriteria: z.array(payeeMatchingCriteriaSchema),
});

const amountSourceConditionSchema = z.object({
  amountMatchingOperator: amountMatchingOperatorSchema,
  amount1: z.number(),
  amount2: z.number().optional(),
});

export const TransactionRuleSchema = z
  .object({
    id: z.string(),
    clientId: z.string(),
    createdAt: z.string(),
    modifiedAt: z.string(),
    payeeSourceCondition: payeeSourceConditionSchema,
    amountSourceCondition: amountSourceConditionSchema,
    coaSourceConditions: z.array(ChartOfAccountSchema),
    accountSourceIds: z.array(z.string()),
    usageSourceType: usageTypeSchema,
    payeeTarget: z.string(),
    useDefaultAccountUsage: z.boolean(),
    coaTargetCondition: ChartOfAccountSchema.optional(),
    businessCoaTargetCondition: ChartOfAccountSchema.optional(),
    tagsTargetIds: z.array(z.string()),
    memoTarget: z.string(),
    isExcludedFromReportsTarget: z.boolean(),
    isExcludedFromF2STarget: z.boolean(),
    isReviewedTarget: z.boolean(),
    hashKey: z.string(),
    businessTargetId: z.string(),
    customerTargetId: z.string(),
    projectTargetId: z.string(),
    applyToExistingTransactions: z.boolean(),
    prevTransactionRuleId: z.number(),
    nextTransactionRuleId: z.number(),
  })
  .partial();

export enum ConditionsEnum {
  PAYEE_SOURCE_CONDITION = 'payeeSourceCondition',
  AMOUNT_SOURCE_CONDITION = 'amountSourceCondition',
  COA_SOURCE_CONDITIONS = 'coaSourceConditions',
  ACCOUNT_SOURCE_IDS = 'accountSourceIds',
  USAGE_SOURCE_TYPE = 'usageSourceType',
  PAYEE_TARGET = 'payeeTarget',
  COA_TARGET_CONDITION = 'coaTargetCondition',
  TAGS_TARGET_IDS = 'tagsTargetIds',
  MEMO_TARGET = 'memoTarget',
  IS_EXCLUDED_FROM_REPORTS_TARGET = 'isExcludedFromReportsTarget',
  IS_EXCLUDED_FROM_F2S_TARGET = 'isExcludedFromF2STarget',
  IS_REVIEWED_TARGET = 'isReviewedTarget',
}

export const TransactionRuleConditionStatusSchema = z.object({
  payeeSourceCondition: z.boolean(),
  amountSourceCondition: z.boolean(),
  coaSourceConditions: z.boolean(),
  accountSourceIds: z.boolean(),
  usageSourceType: z.boolean(),
  payeeTarget: z.boolean(),
  coaTargetCondition: z.boolean(),
  tagsTargetIds: z.boolean(),
  memoTarget: z.boolean(),
  isExcludedFromReportsTarget: z.boolean(),
  isExcludedFromF2STarget: z.boolean(),
  isReviewedTarget: z.boolean(),
});

export type TransactionRuleConditionStatusType = z.infer<typeof TransactionRuleConditionStatusSchema>;
export type TransactionRuleConditionStatusTypeKeys = keyof z.infer<typeof TransactionRuleConditionStatusSchema>;
export type TransactionRuleType = z.infer<typeof TransactionRuleSchema>;
export type PayeeSourceConditionType = z.infer<typeof payeeSourceConditionSchema>;
export type AmountSourceConditionType = z.infer<typeof amountSourceConditionSchema>;
export type AmountSourceMatchingType = z.infer<typeof amountMatchingOperatorSchema>;
export type CategorySourceConditionsType = z.infer<typeof ChartOfAccountSchema>;
export type CategoryTargetConditionsType = z.infer<typeof ChartOfAccountSchema>;
export type UsageTypeSourceConditionsType = z.infer<typeof usageTypeSchema>;
export type PayeeNameType = z.infer<typeof payeeNameTypeSchema>;
export type PayeeMatchingType = z.infer<typeof payeeMatchingCriteriaSchema>;
export type PayeeMatchingOperatorType = z.infer<typeof payeeMatchingOperatorSchema>;
export type ServiceLevelType = z.infer<typeof serviceLevel>;

export type TransactionRulesState = QcsResourceSlice<TransactionRuleType>;
export enum ResolutionTypeEnum {
  CLOSE = 'close', // exit txn rule flow
  EDIT = 'editExistingRule', // conflict - edit existing
  REPLACE = 'replaceWithNewRule', // conflict - replace
  CREATE = 'createNewRule', // no conflict create
  UPDATE = 'updateExistingRule', // no conflict edit
}

export type ResolutionUnion = keyof typeof ResolutionTypeEnum;

// defaults
export const defaultPayeeSourceCondition: PayeeSourceConditionType = {
  payeeType: 'STATEMENT',
  payeeMatchingCriteria: [
    {
      payeeMatchingOperator: 'CONTAINS',
      payeeName: undefined,
    },
  ],
};

declare module 'app-components/utils/redux-store/rootState' {
  export default interface RootState {
    [TRANSACTION_RULES_REDUCER_KEY]: TransactionRulesState;
  }
}

export type RuleState = {
  serviceLevel: ServiceLevelType;
  stage?: TransactionRulesStages;

  buttonHelperText: string;
  buttonText: string;
  backText: string;
  titleText: string;

  rule?: TransactionRuleType;
  onUpdate: (rule?: TransactionRuleType) => void;
  duplicateId?: string;
  error?: string;
  fromTxn: boolean;
  editField?: TransactionRuleConditionStatusTypeKeys;
  inferredPayeeName?: string;
  statementPayeeName?: string;
};

export type ContextType = {
  state: RuleState;
  dispatch: React.Dispatch<any>;
};

export type RuleProviderProps = {
  rule: TransactionRuleType;
  onUpdate: (rule?: TransactionRuleType) => void;
  children?: React.ReactNode;
  fromTxn?: boolean;
  editField?: TransactionRuleConditionStatusTypeKeys;
  inferredPayeeName?: string;
  statementPayeeName?: string;
};

export type SubmitPayload = {
  transactionRule: TransactionRuleType;
  enabled: TransactionRuleConditionStatusType;
};

export type SubmitHandler<Payload, AdditionalArg = void> = (args: {
  payload: Payload;
  additionalArg: AdditionalArg;
}) => Promise<void> | void;

export const UPDATE_SERVICE_LEVEL = 'UPDATE_SERVICE_LEVEL';
export const UPDATE_STAGE = 'UPDATE_STAGE';
export const UPDATE_APPLY_TO_EXISTING = 'UPDATE_APPLY_TO_EXISTING';
export const UPDATE_RULE = 'UPDATE_RULE';
export const HANDLE_SUBMIT = 'HANDLE_SUBMIT';
export const HANDLE_BACK_NAVIGATION = 'HANDLE_BACK_NAVIGATION';
export const UPDATE_DUPLICATE_ID = 'UPDATE_DUPLICATE_ID';
export const UPDATE_ERROR = 'UPDATE_ERROR';
export const UPDATE_FROM_TXN = 'UPDATE_FROM_TXN';

export type RuleAction = TaggedUnion<
  'type',
  {
    UPDATE_SERVICE_LEVEL: { payload: ServiceLevelType };
    UPDATE_STAGE: { payload: TransactionRulesStages };
    UPDATE_APPLY_TO_EXISTING: { payload: boolean };
    UPDATE_RULE: { payload: TransactionRuleType };
    HANDLE_SUBMIT: { payload: TransactionRulesStages };
    HANDLE_BACK_NAVIGATION: { payload: TransactionRulesStages };
    UPDATE_DUPLICATE_ID: { payload: string };
    UPDATE_ERROR: { payload: string | undefined };
    UPDATE_FROM_TXN: { payload: boolean };
  }
>;

export const MAX_OR_CONDITIONS = 50;
